import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { AbstractService } from '../../abstractions/abstract-typed.service';
import { Globals } from '../../app.globals';
import { Question } from './forms.service';

@Injectable()
export class QRCodeService extends AbstractService<QRCodeSetup>
{
    formArea: string;
    formEntryNumber: number;

    constructor(public http: HttpClient, public globals: Globals) {
        super(http, globals);
    }

    getApiUrl() {
        return "/api/settings/";
    }

    create(qrCodeSetup: any): Observable<any> {
        this.showSpinner();
        return this.http
            .post(
                this.getApiUrl() +
                "qrcode/" +
                "forms/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "create",
                qrCodeSetup
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    update(qrCodeSetup: any): Observable<any> {
        this.showSpinner();
        return this.http
            .post(
                this.getApiUrl() +
                "qrcode/" +
                "forms/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "update",
                qrCodeSetup
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    getAll() {
        return this.http
            .get(
                this.getApiUrl() +
                "qrcode/" +
                "forms/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "getall/"
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    getAllActive() {
        return this.http
            .get(
                this.getApiUrl() +
                "qrcode/" +
                "forms/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "getallactive/"
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }

    delete(entryNumber: number) {
        return this.http
            .delete(
                this.getApiUrl() +
                "qrcode/" +
                "forms/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "delete/" +
                entryNumber
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    activate(entryNumber: number) {
        return this.http
            .put(
                this.getApiUrl() +
                "qrcode/" +
                "forms/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "activate/" +
                entryNumber,
                null
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    deactivate(entryNumber: number) {
        return this.http
            .put(
                this.getApiUrl() +
                "qrcode/" +
                "forms/" +
                this.formArea + "/" +
                this.formEntryNumber + "/" +
                "deactivate/" +
                entryNumber,
                null
            )
            .pipe(
                catchError(this.handleError),
                finalize(() => this.hideSpinner())
            );
    }

    generateQRCode(qrFormRequest: any, formEntryNumber: number): Observable<string> {
        return this.http
            .post(this.getApiUrl() +
                "qrcode/" +
                "forms/" +
                this.formArea + "/" +
                formEntryNumber + "/" +
                "generateqrcode", qrFormRequest,
                {
                    responseType: 'text'
                }
            )
            .pipe(
                map((res) => res),
                catchError(this.handleError)
            );
    }
}

export class QRCodeSetup {
    id?: number;
    formId: number;
    entryNumber: number;
    name: string;
    roleAccessType: string;
    isActive: boolean;
    modifiedBy: string;
    modifiedById: number;
    modified: Date;
    createdBy: string;
    createdById: number;
    created: Date;
    qrCodeRoleDetails: QRCodeRoleDetail[];
    qrCodeTypeDetails: QRCodeTypeDetail[];

    constructor() {
        this.id = 0;
        this.formId = 0;
        this.entryNumber = 0;
        this.name = "";
        this.roleAccessType = "Non Role Based";
        this.isActive = false;
        this.modifiedBy = "";
        this.modifiedById = null;
        this.modified = null;
        this.createdBy = "";
        this.created = new Date();
        this.createdById = 0;
        this.qrCodeRoleDetails = [];
        this.qrCodeTypeDetails = [];
    }

    static map(value: any): QRCodeSetup {
        if (!value) return new QRCodeSetup();
        return {
            id: value.id,
            formId: value.formId,
            entryNumber: value.entryNumber,
            name: value.name,
            roleAccessType: value.roleAccessType,
            isActive: value.isActive,
            modifiedBy: value.modifiedBy,
            modifiedById: value.modifiedById,
            modified: value.modified,
            createdBy: value.createdBy,
            createdById: value.createdById,
            created: value.created,
            qrCodeRoleDetails: value.qrCodeRoleDetails,
            qrCodeTypeDetails: value.qrCodeTypeDetails
        };
    }
}

export class QRCodeRoleDetail {
    id?: number;
    qrCodeSetupId?: number;
    roleId?: number;
    type: string;

    static map(value: any): QRCodeRoleDetail {
        if (!value) return new QRCodeRoleDetail();
        return {
            id: 0,
            qrCodeSetupId: value.qrCodeSetupId,
            roleId: value.roleId,
            type: value.type
        };
    }
}

export class QRCodeTypeDetail {
    id?: number;
    qrCodeSetupId?: number;
    accessType: string;
    type: string;
    qrCodeQuestions: QRCodeQuestion[];

    static map(value: any): QRCodeTypeDetail {
        if (!value) return new QRCodeTypeDetail();
        return {
            id: 0,
            qrCodeSetupId: value.qrCodeSetupId,
            accessType: value.accessType,
            type: value.type,
            qrCodeQuestions: value.qrCodeQuestions
        };
    }
}

export class QRCodeQuestion {
    id?: number;
    qrCodeTypeId?: number;
    questionId: number;
    value: string;

    static map(value: any): QRCodeQuestion {
        if (!value) return new QRCodeQuestion();
        return {
            id: 0,
            qrCodeTypeId: value.qrCodeTypeId,
            questionId: value.questionId,
            value: value.value
        };
    }
}

export enum QRCodeTypes {
    Create = 0,
    Update,
    View,
    Populate_1,
    Populate_2,
    Populate_3,
    Populate_4
}

export enum QRCodeRoleAccessType {
    RoleBased,
    NonRoleBased
}

export enum AccessTypes {
    Public,
    Private
}

export class Status {
    static readonly Active = "Active";
    static readonly Inactive = "Inactive";
}


export class Options {
    static readonly Edit = "Edit";
    static readonly View = "View";
    static readonly Activate = "Activate";
    static readonly Deactivate = "Deactivate";
    static readonly Remove = "Remove";
}

export class QRFormRequest {
    isCreateTypeQRCode: boolean;
    projectId: number;
    fcEntryNumber: number;
    qrCodeEntryNumber: number;
    accessNbr: number;
    routeUrl: string;
    isGlobal: boolean;
}
